import { z } from 'zod';

export const layoutVisualizationTypes = ['swimlane', 'timeline', 'table'] as const;
export const layoutVisualizationTypeSchema = z.enum(layoutVisualizationTypes);

export const dateHeaderTypes = [
  'startDate:week',
  'startDate:month',
  'startDate:quarter',
  'startDate:half',
  'startDate:year',
  'endDate:week',
  'endDate:month',
  'endDate:quarter',
  'endDate:half',
  'endDate:year',
] as const;

export const headerDirections = ['horizontal', 'vertical'] as const;
export const headerDirectionSchema = z.enum(headerDirections);

export const themes = ['standard', 'compact'] as const;
export const themeSchema = z.enum(themes);

export const tableDensities = ['compact', 'standard', 'comfortable'] as const;
export const tableDensitySchema = z.enum(tableDensities);
export type TableDensity = z.infer<typeof tableDensitySchema>;

export const DEFAULT_TABLE_DENSITY: TableDensity = 'standard';

export const titleSizes = ['small', 'medium', 'large', 'extra-large'] as const;
export const titleSizeSchema = z.enum(titleSizes);
export type TitleSize = z.infer<typeof titleSizeSchema>;

export const titleHeights = ['show-less', 'default', 'show-more'] as const;
export const titleHeightSchema = z.enum(titleHeights);
export type TitleHeight = z.infer<typeof titleHeightSchema>;

export const progressFieldTypes = ['custom', 'system'] as const;
export const progressFieldTypeSchema = z.enum(progressFieldTypes);
export type ProgressFieldType = z.infer<typeof progressFieldTypeSchema>;

export const childItemLevelsSchema = z.union([z.literal(1), z.literal(2)]);
export type ChildItemLevels = z.infer<typeof childItemLevelsSchema>;

export const headerTypes = ['status', 'parentItem', 'team', 'createdBy', 'estimationId'] as const;
export const allHeaderTypes = [...headerTypes, ...dateHeaderTypes] as const;
export const headerLabelsType = [...headerTypes, 'itemDate'] as const;

export type HeaderLabelsType = (typeof headerLabelsType)[number];
export type HeaderType = (typeof headerLabelsType)[number];

export const HEADER_TYPE_TRANSLATIONS = {
  status: 'loopVisualization.layout.headers.fields.status',
  parentItem: 'loopVisualization.layout.headers.fields.parentItem',
  team: 'loopVisualization.layout.headers.fields.team',
  itemDate: 'loopVisualization.layout.headers.fields.date',
  createdBy: 'loopVisualization.layout.headers.fields.createdBy',
  estimationId: 'loopVisualization.layout.headers.fields.estimation',
} as const satisfies Record<HeaderLabelsType, string>;

export const allowedHeaderCustomFieldTypes = [
  'PICKLIST',
  'MULTI_PICKLIST',
  'MULTI_CUSTOMER',
  'USER',
  'MULTI_USER',
] as const;
export type AllowedCustomFieldType = (typeof allowedHeaderCustomFieldTypes)[number];

export const layoutHeaderSchema = z.string().refine(
  (val) => {
    if (allHeaderTypes.includes(val as (typeof allHeaderTypes)[number])) {
      return true;
    }
    return /^[1-9]\d*$/.test(val) ? Number.parseInt(val, 10) <= Number.MAX_SAFE_INTEGER : false;
  },
  {
    error: `Invalid header specified. Header must be one of ['${allHeaderTypes.join("', '")}'] or a custom field id.`,
  },
);

export const layoutHeaderDTOSchema = z.nullable(layoutHeaderSchema);
export const layoutHeaderUpsertDTOSchema = z.optional(layoutHeaderDTOSchema);

export const colorConfigSchema = z
  .object({
    mode: z.enum(['baseColor', 'palette']),
    baseColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    paletteId: z.string().optional(),
    colorByField: z.string().nullable().optional(),
    itemColorMap: z.record(z.string(), z.number()).optional(),
    matchHeaderColors: z.boolean().optional(),
    headerColorOrder: z.array(z.string()).optional(),
    desaturatedHeaderColorIndex: z.number().min(0).max(2).optional(),
  })
  .nullable()
  .optional();

export type ColorConfig = z.infer<typeof colorConfigSchema>;

export const baseLayoutDTOSchema = z.object({
  itemLevelId: z.number(),
  visualizationId: z.number().int().positive(),
  theme: themeSchema,
});

export const canvasBaseLayoutDTOSchema = baseLayoutDTOSchema.extend({
  // Allow for header1 and header2 to be null
  header1: layoutHeaderDTOSchema,
  header2: layoutHeaderDTOSchema,
  headerDirection: headerDirectionSchema,
  showEmptyHeaders: z.boolean().default(false),

  colorConfig: colorConfigSchema,

  // Label by headers for card labels
  labelByHeader1: layoutHeaderDTOSchema,
  labelByHeader2: layoutHeaderDTOSchema,

  // Custom header ordering - array of header IDs in desired order
  columnHeaderOrder: z.array(z.string()).optional(),
  rowHeaderOrder: z.array(z.string()).optional(),
  combineMultiSelectHeaders: z.boolean().optional(),

  // Row header width for resizable divider (min 120, max 600)
  rowHeaderWidth: z.number().int().min(120).max(600).optional(),
});

export const swimlaneLayoutDTOSchema = z.object({
  ...canvasBaseLayoutDTOSchema.shape,
  visualizationType: z.literal('swimlane'),
  groupRowsByHeader: layoutHeaderDTOSchema,
  showItemDates: z.boolean().default(true),
  showGridLines: z.boolean().default(true),
  showDescription: z.boolean().default(true),
  showParent: z.boolean().default(true),
  showChildItems: z.boolean().default(true),
  childItemLevels: childItemLevelsSchema.default(1),
  titleSize: titleSizeSchema.default('medium'),
  titleHeight: titleHeightSchema.default('default'),
  progressFieldType: progressFieldTypeSchema.optional().default('custom'),
  progressFieldId: z.number().int().positive().nullable().optional(),
  combineMultiSelectHeaders: z.boolean().optional(),
});

export type SwimlaneLayoutDTO = z.infer<typeof swimlaneLayoutDTOSchema>;

export const timelineLayoutDTOSchema = z.object({
  ...canvasBaseLayoutDTOSchema.shape,
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  visualizationType: z.literal('timeline'),
  showTodayLine: z.boolean().default(true),
  titleSize: titleSizeSchema.default('medium'),
  progressFieldType: progressFieldTypeSchema.optional().default('custom'),
  progressFieldId: z.number().int().positive().nullable().optional(),
  showChildItems: z.boolean().default(true),
  combineMultiSelectHeaders: z.boolean().optional(),
});

export type TimelineLayoutDTO = z.infer<typeof timelineLayoutDTOSchema>;

/**
 * Shared default swimlane layout, used by every consumer (frontend and backend
 * published/preview) so a visualization with no saved layout renders identically.
 * Callers supply itemLevelId (lowest item-type level for the view).
 */
export const getDefaultSwimlaneLayout = (
  visualizationId: number,
  itemLevelId: number,
): SwimlaneLayoutDTO => ({
  visualizationType: 'swimlane',
  visualizationId,
  itemLevelId,
  header1: 'status',
  header2: 'team',
  groupRowsByHeader: null,
  headerDirection: 'vertical',
  theme: 'standard',
  showEmptyHeaders: false,
  showItemDates: true,
  showGridLines: true,
  showDescription: true,
  showParent: true,
  showChildItems: true,
  childItemLevels: 1,
  titleSize: 'medium',
  titleHeight: 'default',
  colorConfig: null,
  progressFieldType: 'custom',
  labelByHeader1: null,
  labelByHeader2: null,
});

/**
 * Shared default timeline layout. See getDefaultSwimlaneLayout for the rationale.
 */
export const getDefaultTimelineLayout = (
  visualizationId: number,
  itemLevelId: number,
): TimelineLayoutDTO => ({
  visualizationType: 'timeline',
  visualizationId,
  itemLevelId,
  header1: 'status',
  header2: 'team',
  headerDirection: 'horizontal',
  theme: 'standard',
  showEmptyHeaders: false,
  colorConfig: null,
  labelByHeader1: null,
  labelByHeader2: null,
  showTodayLine: true,
  titleSize: 'medium',
  progressFieldType: 'custom',
  progressFieldId: null,
  showChildItems: true,
});

// Item fields that can be used as table columns (from item-dto, plus virtual fields like 'progress' that are fetched separately)
export const TABLE_COLUMN_ITEM_FIELDS = [
  'name',
  'externalKey',
  'timeSpent',
  'targetStartDate',
  'targetFinishDate',
  'plannedStartDate',
  'plannedFinishDate',
  'doneDate',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'status',
  'team',
  'estimation',
  'estimationScheme',
  'itemType',
  'progress',
  'health',
  'timeSpentVsPlanned',
  'integration',
] as const;
type ItemField = (typeof TABLE_COLUMN_ITEM_FIELDS)[number];

// Schedule fields that can be used as table columns
export const TABLE_COLUMN_SCHEDULE_FIELDS = [
  'expectedStartDate',
  'expectedFinishDate',
  'latestFinishDate',
] as const;

type ScheduleField = (typeof TABLE_COLUMN_SCHEDULE_FIELDS)[number];

export type TableColumnSystemField = ItemField | ScheduleField;
export const TABLE_COLUMN_SYSTEM_FIELDS = [
  ...TABLE_COLUMN_ITEM_FIELDS,
  ...TABLE_COLUMN_SCHEDULE_FIELDS,
] as const satisfies readonly TableColumnSystemField[];

export type TableColumnTranslationKey =
  `views.table.columns.systemFields.${TableColumnSystemField}`;
export const TABLE_COLUMN_SYSTEM_FIELDS_TRANSLATIONS: ReadonlyMap<
  TableColumnSystemField,
  TableColumnTranslationKey
> = new Map(
  TABLE_COLUMN_SYSTEM_FIELDS.map((field) => [
    field,
    `views.table.columns.systemFields.${field}` as TableColumnTranslationKey,
  ]),
);

export const REQUIRED_TABLE_COLUMN_FIELDS: ReadonlySet<TableColumnSystemField> = new Set(['name']);

export const ALL_TABLE_COLUMN_TYPES = [...TABLE_COLUMN_SYSTEM_FIELDS, 'customField'] as const;

export const tableColumnIdentifierSchema = z.object({
  type: z.enum(ALL_TABLE_COLUMN_TYPES as unknown as [string, ...string[]]),
  customFieldId: z.number().nullish(),
});
export type TableColumnIdentifier = z.infer<typeof tableColumnIdentifierSchema>;

export const tableColumnDTOSchema = tableColumnIdentifierSchema.extend({
  width: z.number().optional(),
});
export type TableColumnDTO = z.infer<typeof tableColumnDTOSchema>;

export const SortSchema = tableColumnIdentifierSchema.extend({
  order: z.enum(['asc', 'desc']),
  collate: z.boolean().optional(),
});
export type SortDto = z.infer<typeof SortSchema>;

export const tableLayoutDTOSchema = baseLayoutDTOSchema.extend({
  visualizationType: z.literal('table'),
  columns: z.array(tableColumnDTOSchema),
  sort: SortSchema.optional(),
  density: tableDensitySchema,
});

export type TableLayoutDTO = z.infer<typeof tableLayoutDTOSchema>;

export const DEFAULT_TABLE_COLUMN_WIDTH = 150;

/**
 * Default columns for new table layouts.
 * Shared between client and server to ensure consistency.
 */
export const DEFAULT_TABLE_COLUMNS: TableColumnDTO[] = [
  { type: 'name', customFieldId: null },
  { type: 'itemType', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'externalKey', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'estimation', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'status', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'team', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'plannedStartDate', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'plannedFinishDate', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'integration', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
];

/**
 * Default columns for a freshly opened Scenario table.
 * Mirrors DEFAULT_TABLE_COLUMNS but uses the scenario schedule dates
 * (expected start/finish) in place of the planned dates, and omits the
 * `integration` column (scenarios show only the curated 8-column set).
 */
export const DEFAULT_SCENARIO_TABLE_COLUMNS: TableColumnDTO[] = [
  { type: 'name', customFieldId: null },
  { type: 'itemType', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'externalKey', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'estimation', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'status', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'team', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'expectedStartDate', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
  { type: 'expectedFinishDate', customFieldId: null, width: DEFAULT_TABLE_COLUMN_WIDTH },
];

export const layoutDTOSchema = z.discriminatedUnion('visualizationType', [
  swimlaneLayoutDTOSchema,
  timelineLayoutDTOSchema,
  tableLayoutDTOSchema,
]);

export type LayoutDTO = z.infer<typeof layoutDTOSchema>;

export const baseLayoutUpsertDTOSchema = z.object({
  visualizationType: layoutVisualizationTypeSchema,
  itemLevelId: z.number().int().positive(),
  theme: themeSchema.optional(),
});

export const canvasLayoutUpsertDTOSchema = baseLayoutUpsertDTOSchema.extend({
  header1: z.optional(layoutHeaderDTOSchema),
  header2: z.optional(layoutHeaderDTOSchema),
  headerDirection: headerDirectionSchema.optional(),
  showEmptyHeaders: z.boolean().optional(),
  colorConfig: colorConfigSchema,
  labelByHeader1: z.optional(layoutHeaderDTOSchema),
  labelByHeader2: z.optional(layoutHeaderDTOSchema),
  rowHeaderWidth: z.number().int().min(120).max(600).optional(),
});

export type CanvasLayoutUpsertDTO = z.infer<typeof canvasLayoutUpsertDTOSchema>;

export const upsertSwimlaneLayoutDTOSchema = z
  .object({
    ...canvasLayoutUpsertDTOSchema.shape,
    visualizationType: z.literal('swimlane'),
    groupRowsByHeader: z.optional(layoutHeaderDTOSchema),
    showItemDates: z.boolean().optional(),
    showGridLines: z.boolean().optional(),
    showDescription: z.boolean().optional(),
    showParent: z.boolean().optional(),
    showChildItems: z.boolean().optional(),
    childItemLevels: childItemLevelsSchema.optional(),
    titleSize: titleSizeSchema.optional(),
    titleHeight: titleHeightSchema.optional(),
    progressFieldType: progressFieldTypeSchema.optional(),
    progressFieldId: z.number().int().positive().nullable().optional(),
    combineMultiSelectHeaders: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.groupRowsByHeader && !data.header2) {
      ctx.addIssue({
        code: 'custom',
        message: 'Cannot set groupRowsByHeader without a row header (header2)',
        path: ['groupRowsByHeader'],
      });
    }
  });

export type SwimlaneLayoutUpsertDTO = z.infer<typeof upsertSwimlaneLayoutDTOSchema>;

export const upsertTimelineLayoutDTOSchema = z.object({
  ...canvasLayoutUpsertDTOSchema.shape,
  visualizationType: z.literal('timeline'),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  showTodayLine: z.boolean().optional(),
  titleSize: titleSizeSchema.optional(),
  progressFieldType: progressFieldTypeSchema.optional(),
  progressFieldId: z.number().int().positive().nullable().optional(),
  showChildItems: z.boolean().optional(),
  combineMultiSelectHeaders: z.boolean().optional(),
});

export type UpsertTimelineLayoutDTO = z.infer<typeof upsertTimelineLayoutDTOSchema>;

export const upsertTableLayoutDTOSchema = baseLayoutUpsertDTOSchema.extend({
  visualizationType: z.literal('table'),
  columns: z.array(tableColumnDTOSchema).optional(),
  sort: SortSchema.optional(),
  density: tableDensitySchema.optional(),
});

export type TableLayoutUpsertDTO = z.infer<typeof upsertTableLayoutDTOSchema>;

export const layoutUpsertDTOSchema = z
  .discriminatedUnion('visualizationType', [
    upsertSwimlaneLayoutDTOSchema,
    upsertTimelineLayoutDTOSchema,
    upsertTableLayoutDTOSchema,
  ])
  .superRefine((data, ctx) => {
    // Prevent duplicate header selections - header1 and header2 must be different
    // Only validate for canvas layouts (swimlane/timeline)
    if (['swimlane', 'timeline'].includes(data.visualizationType)) {
      const canvasData = data as CanvasLayoutUpsertDTO;
      if (canvasData.header1 && canvasData.header1 === canvasData.header2) {
        ctx.addIssue({
          code: 'custom',
          message: 'Header 1 and Header 2 cannot be the same',
          path: ['header2'],
        });
      }
    }
  });

export type LayoutUpsertDTO = z.infer<typeof layoutUpsertDTOSchema>;

export const headerOrderDTOSchema = z.object({
  headerFieldId: layoutHeaderDTOSchema,
  parentFieldHeaderId: layoutHeaderDTOSchema,
  parentHeaderId: z.string().nullable().optional(),
  orderedHeaderIds: z.array(z.string()),
});

export type HeaderOrderDTO = z.infer<typeof headerOrderDTOSchema>;

export const headerOrderUpsertDTOSchema = z.object({
  parentFieldHeaderId: layoutHeaderDTOSchema,
  parentHeaderId: z.string().nullable().optional(),
  orderedHeaderIds: z.array(z.string()),
});

export type HeaderOrderUpsertDTO = z.infer<typeof headerOrderUpsertDTOSchema>;

export const headerHiddenDTOSchema = z.object({
  headerFieldId: layoutHeaderDTOSchema,
  hiddenHeaderIds: z.array(z.string()),
});

export const headerHiddenUpsertDTOSchema = z.object({
  headerId: z.string(),
  parentFieldHeaderId: layoutHeaderDTOSchema,
  parentHeaderId: z.string().nullable().optional(),
});

export type HeaderHiddenDTO = z.infer<typeof headerHiddenDTOSchema>;

export const headerConfigDTOSchema = headerOrderDTOSchema
  .extend(headerHiddenDTOSchema.shape)
  .extend({
    subHeaderOrderings: z.record(z.string(), z.array(z.string())).optional(),
    subHeaderHidden: z.record(z.string(), z.array(z.string())).optional(),
  });
export type HeaderConfigDTO = z.infer<typeof headerConfigDTOSchema>;
